import AppButton from '@nxsys/components/AppButton';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import useAEPNSocket from '@nxsys/hooks/useAEPNSocket';
import { AppDispatch, AppState } from '@nxsys/store';
import { aepnActions } from '@nxsys/store/slices/aepn/reducer/aepnSlice';
import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type Props = {
  isOpen: boolean;
  toggle: () => void;
  taxPeriod?: number | null;
  payFrequency?: string | null;
  monthConfig?: number | null;
  companyRefs?: string[];
  search?: string;
  sortField?: string;
  sortType?: string;
  departments?: string[];
};

const AEPNValidationModal: React.FC<Props> = ({
  isOpen,
  toggle,
  taxPeriod,
  payFrequency,
  monthConfig,
  companyRefs,
  search,
  sortField,
  sortType,
  departments,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: AppState) => state.appStore);

  const [employeeList, setEmployeeList] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 0,
    pageSize: 20,
    totalDocs: 0,
  });
  const [loading, setLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const buildParams = useCallback(
    (page: number, size: number) => {
      const taxYearValue = getTaxWeekTaxYear().taxYear;
      const params: Record<string, any> = {
        page,
        size,
        paymentModel: 'PAYEE',
        invoiceStatus: 'AWAITING_DRAFT',
        onlySavedRunPayroll: true,
        taxYear: taxYearValue,
      };
      if (taxPeriod) params.taxPeriod = taxPeriod;
      if (payFrequency) params.payFrequency = payFrequency;
      if (monthConfig) params.monthConfig = monthConfig;
      if (sortField) params.sortField = sortField;
      if (sortType) params.sortType = sortType;
      if (search) params.search = search;
      if (companyRefs?.length) params.companyRefs = companyRefs;
      if (departments?.length && companyRefs?.length)
        params.departments = departments;
      return params;
    },
    [
      taxPeriod,
      payFrequency,
      monthConfig,
      companyRefs,
      search,
      sortField,
      sortType,
      departments,
    ],
  );

  const fetchData = useCallback(
    async (page = 0, pageSize = 20) => {
      setLoading(true);
      try {
        const params = buildParams(page, pageSize);
        const response = await jwtAxios.get(
          endPoints.timesheet.employeeHaveTimesheet,
          { params },
        );
        setEmployeeList(response.data.data ?? []);
        setPagination({
          current: page,
          pageSize,
          totalDocs: response.data.totalDocs ?? 0,
        });
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [buildParams],
  );

  useEffect(() => {
    if (isOpen) fetchData(0, pagination.pageSize);
  }, [isOpen]);

  const fetchCompanyIds = async (): Promise<string[]> => {
    const params = buildParams(0, 9999);
    const companyRes = await jwtAxios.get(
      endPoints.timesheet.companyHaveTimesheet,
      { params },
    );
    const companyData: any[] = companyRes.data?.data ?? [];
    return [...new Set(companyData.map((item: any) => item.id as string))];
  };

  const handleUpdateAEPN = async () => {
    setIsUpdating(true);
    try {
      const companyIds = await fetchCompanyIds();
      const res = await dispatch(
        aepnActions.aepnDownload({ companyIds }),
      ).unwrap();
      if (res?.success) {
        showMessage({
          type: 'info',
          content: 'Starting update AEPN. Please wait...',
        });
      } else {
        showMessage({
          type: 'error',
          content: res?.message || 'Fail to start update AEPN.',
        });
        setIsUpdating(false);
      }
    } catch (error) {
      console.error(error);
      setIsUpdating(false);
    }
  };

  useAEPNSocket({
    eventName: `naersa-aepn-download-all-${user?.id}`,
    onSuccess: (data) => {
      setIsUpdating(false);
      const errors =
        data?.results?.filter((r: any) => r.errorMessage) ?? [];
      const successes =
        data?.results?.filter((r: any) => !r.errorMessage) ?? [];
      if (errors.length > 0) {
        showMessage({
          type: 'error',
          content: `Update AEPN failed: ${errors.map((e: any) => `${e.companyId}: ${e.errorMessage}`).join(', ')}`,
        });
        if (successes.length > 0) {
          showMessage({
            type: 'success',
            content: `Update AEPN successfully for employers: ${successes.map((s: any) => s.companyId).join(', ')}`,
          });
        }
      } else {
        showMessage({ type: 'success', content: 'Update AEPN successfully' });
      }
      toggle();
    },
    onError: (error) => {
      setIsUpdating(false);
      showMessage({
        type: 'error',
        content: error || 'Fail to update AEPN.',
      });
    },
  });

  const getRowClassName = (record: any) => {
    if (
      !record?.currentAepn ||
      moment(record?.currentAepn?.createdAt).format('DD/MM/YYYY') !==
        moment().format('DD/MM/YYYY')
    ) {
      return 'row-warning';
    }
    return '';
  };

  const getErrorMessage = (record: any) => {
    if (!record?.currentAepn) {
      return 'The employee has no Auto Enrolment';
    }
    if (
      moment(record?.currentAepn?.createdAt).format('DD/MM/YYYY') !==
      moment().format('DD/MM/YYYY')
    ) {
      return 'Auto Enrolment was not updated today';
    }
    return '_';
  };

  const columns: ColumnsType<any> = [
    {
      key: 'id',
      title: 'Employee ID',
      dataIndex: 'id',
      width: 150,
    },
    {
      key: 'employeeName',
      title: 'Employee Name',
      width: 200,
      render: (_v, record) =>
        record?.fullName ??
        `${record?.forename ?? '_'} ${record?.surname ?? '_'}`,
    },
    {
      key: 'agencyRef',
      title: 'Employer Ref',
      width: 150,
      render: (_v, record) => record?.agencyRef || '',
    },
    {
      key: 'companyPayment',
      title: 'Employer',
      width: 200,
      render: (_v, record) => record?.companyPayment?.companyName || '_',
    },
    {
      key: 'errorMessage',
      title: 'Error Message',
      render: (_v, record) => getErrorMessage(record),
    },
  ];

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    fetchData(shouldResetPage ? 0 : page - 1, pageSize);
  };

  return (
    <AppModalContainer
      title="Update AEPN"
      isOpen={isOpen}
      toggle={toggle}
      width="90vw"
      footer={null}
    >
      <AppsHeader>
        <div className="flex justify-end w-full">
          <AppButton
            name="Update All"
            onClick={handleUpdateAEPN}
            loading={isUpdating}
            disabled={isUpdating}
          />
        </div>
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          data={employeeList}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1200, y: 500 }}
          titleEmpty="No Record"
          columns={columns}
          onChangePagination={onChangePagination}
          rowClassName={getRowClassName}
        />
      </AppsContent>
    </AppModalContainer>
  );
};

export default AEPNValidationModal;
